// Fracture Brood (replaces `splitter`) — low, wide four-legged shell creature: two visibly divided slate carapace halves
// backed with crystals and a vivid magenta seam down the middle. The halves breathe open along the seam as it walks.
// Wind-up (lab pulse; the sim gives splitters none): the head rears and the seam brightens.
// Elite (Brood Matriarch): wider bifurcated carapace with crystal flanges and an enlarged seam with spines.
import * as THREE from 'three/webgpu';
import { ArtBuilder, at, EnemyArt, limb } from './kit';
import { ball, compassDip, gem, noSkateSwing, V3 } from './parts_spec';

const STRIDE = 4.4, HIP = 0.62;
const SW = noSkateSwing(HIP, STRIDE);
const SHELL = 0x5d667c, SHELL_DARK = 0x3c4252, FLESH = 0x2e2c34, CRYS = 0xd88ae8, SEAM = 0xff3ca8, BONE = 0xd9ccb0;

function build(elite: boolean): ReturnType<ArtBuilder['build']> {
  const b = new ArtBuilder();
  const sw = elite ? noSkateSwing(HIP * 1.7, STRIDE) : SW;
  const gap = elite ? 0.1 : 0.06, wx = elite ? 0.8 : 0.68, by = 0.58;
  // belly and flesh core between the halves
  b.add(at(new THREE.SphereGeometry(1, 10, 4, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), 0, by + 0.02, 0, 0, 0, 0, [wx + gap, 0.24, 0.92]), { color: FLESH });
  b.add(at(new THREE.SphereGeometry(1, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2), 0, by, 0, 0, 0, 0, [gap + 0.03, 0.56, 0.9]), { color: SEAM, glow: true }); // seam fin
  for (const s of [-1, 1]) {
    // carapace half: a quarter-ellipsoid, opening outward along the seam with the gait
    const pv: V3 = [s * gap, by, 0], o = { pivot: pv, flap: -s * 0.09, raise: 0 };
    b.add(at(new THREE.SphereGeometry(1, 7, 5, s > 0 ? Math.PI / 2 : -Math.PI / 2, Math.PI, 0, Math.PI / 2), s * gap, by, 0, 0, 0, 0, [wx, 0.6, 0.98]), { color: SHELL, ...o });
    b.add(at(new THREE.CylinderGeometry(1, 1, 0.08, 10, 1, true, s > 0 ? 0 : Math.PI, Math.PI), s * gap, by + 0.02, 0, 0, 0, 0, [wx * 1.02, 1, 1.0]), { color: SHELL_DARK, ...o }); // rim
    // crystal growths (asymmetric per side)
    const gs: [number, number, number, number][] = s > 0
      ? [[0.3, 0.2, 0.62, 0.1], [0.42, -0.2, 0.45, 0.08], [0.22, -0.45, 0.4, 0.07]]
      : [[0.28, 0.0, 0.75, 0.11], [0.4, 0.35, 0.4, 0.07], [0.3, -0.38, 0.5, 0.08]];
    for (const [x, z, h, r] of gs) {
      const ex = x * (wx / 0.68), sy = by + 0.6 * Math.sqrt(Math.max(0, 1 - (ex / wx) ** 2 - (z / 0.98) ** 2));
      b.add(gem(r, h, [s * (gap + ex), sy + h * 0.3, z], 0.15 * -Math.sign(z), 0, -s * (0.25 + x * 0.6)), { color: CRYS, ...o });
    }
    if (elite) {
      // Matriarch: crystal flanges flaring out from each half (bifurcated carapace) and seam spines
      for (const z of [0.35, -0.05, -0.45]) b.add(gem(0.09, 0.62, [s * (gap + wx * 0.92), by + 0.22, z], 0, 0, -s * 1.15), { color: CRYS, ...o });
    }
  }
  if (elite) for (const z of [0.5, 0.15, -0.2, -0.55]) b.add(gem(0.05, 0.36, [0, by + 0.6 * Math.sqrt(1 - (z / 0.9) ** 2) + 0.08, z]), { color: SEAM, glow: true });
  // head: low basalt wedge with bone mandibles and two magenta eyes; rears on the telegraph
  const hp: V3 = [0, 0.55, 0.85], H = { pivot: hp, raise: -0.35 };
  b.add(ball(0.26, [0, 0.55, 1.0], [1.15, 0.7, 0.95], 10, 6), { color: FLESH, ...H });
  b.add(ball(0.2, [0, 0.66, 0.98], [1.2, 0.5, 0.9], 8, 5), { color: SHELL_DARK, ...H });
  for (const s of [-1, 1]) {
    b.add(limb([s * 0.12, 0.47, 1.12], [s * 0.05, 0.42, 1.38], 0.045, 0.012, 5), { color: BONE, ...H });
    b.add(ball(0.035, [s * 0.11, 0.62, 1.2], [1, 1, 1], 6, 4), { color: SEAM, glow: true, ...H });
  }
  // four legs, diagonal pairs (front-left & back-right phase 0). +X is the creature's left.
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const hip: V3 = [sx * (wx * 0.75), HIP, sz * 0.48], knee: V3 = [sx * (wx + 0.3), 0.92, sz * 0.62], foot: V3 = [sx * (wx + 0.26), 0.0, sz * 0.72];
    const o = { pivot: hip, swing: sw, sphase: sx * sz > 0 ? 0 : Math.PI };
    b.add(limb(hip, knee, 0.1, 0.08, 7), { color: FLESH, ...o });
    b.add(limb(knee, foot, 0.08, 0.025, 7), { color: SHELL_DARK, ...o });
    b.add(ball(0.09, knee, [1, 1, 1], 7, 5), { color: SHELL, ...o });
    b.add(gem(0.05, 0.18, [knee[0] + sx * 0.04, knee[1] + 0.1, knee[2]], 0, 0, -sx * 0.4), { color: CRYS, ...o });
  }
  return b.build();
}

export const ART: EnemyArt = { name: 'Fracture Brood', build, stride: STRIDE, bob: compassDip(HIP, SW) * 0.85 };
